'use client';

import { Check } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

interface PickerRowProps {
  title: string;
  description?: string;
  /** Optional leading element — thumbnail (44×32), icon, or color-swatch row.
   *  Mirrors Apple `SelectableImageRow` / `SelectableRow<Leading>`. */
  leading?: React.ReactNode;
  /** Optional element rendered next to the title (e.g. PRO badge). */
  trailing?: React.ReactNode;
  /** Optional element pinned to the far right, before the selected checkmark
   *  (e.g. a thumbnail strip on a reference-source row). */
  accessory?: React.ReactNode;
  isSelected?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
  className?: string;
}

/**
 * Unified picker row for render-settings sub-pickers (Materials, Environment,
 * Style, Output, Model, Colors, ...). Apple parity: button + optional leading
 * (thumbnail / icon / swatches) + title + optional description + trailing
 * checkmark when selected. Lives inside a `SettingsCard` (light-gray grouped
 * surface) and uses a hairline divider between rows.
 *
 * Mirrors `SelectableTextRow` / `SelectableDescriptionRow` /
 * `SelectableImageRow` / `SelectableRow` from
 * `apps/apple/.../UI/Components/SelectableRowView.swift`.
 */
export function PickerRow({
  title,
  description,
  leading,
  trailing,
  accessory,
  isSelected = false,
  isDisabled = false,
  onClick,
  className,
}: PickerRowProps) {
  return (
    <button
      type="button"
      disabled={isDisabled}
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 py-3 px-4 text-left',
        // Visible iOS-style hairline. The `--border` token is oklch(0.94) —
        // nearly invisible on the grouped card fill — so a faded `/40` of it
        // vanished entirely; a tinted muted-foreground reads like the native
        // inset-grouped separator.
        'border-b border-muted-foreground/25 last:border-b-0',
        'transition-colors',
        isDisabled
          ? 'opacity-50 cursor-not-allowed'
          : 'cursor-pointer hover:bg-foreground/5 active:bg-foreground/10',
        className
      )}
    >
      {leading && <div className="flex-shrink-0">{leading}</div>}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={cn(
              'text-card-body truncate',
              isDisabled ? 'text-muted-foreground' : 'text-foreground'
            )}
          >
            {title}
          </span>
          {trailing && <span className="flex-shrink-0">{trailing}</span>}
        </div>
        {description && (
          <p className="text-list-item-subtitle text-muted-foreground mt-0.5 line-clamp-2">
            {description}
          </p>
        )}
      </div>

      {accessory && <div className="flex-shrink-0">{accessory}</div>}

      {isSelected && (
        <Check
          className="w-5 h-5 text-primary flex-shrink-0"
          strokeWidth={2.5}
        />
      )}
    </button>
  );
}
